import { InboxOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, Select, Table, Tag, Upload, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { Dragger } = Upload;
const { Option } = Select;

const REQ_STATUS_COLOR = { pending: "gold", approved: "blue", completed: "green", rejected: "red" };
const REQ_STATUS_LABEL = { pending: "Pending Approval", approved: "Approved - Awaiting Generation", completed: "Completed", rejected: "Rejected" };

const FORM_TYPE_LABEL = {
  transfer_certificate: "Transfer Certificate",
  study_certificate: "Study Certificate",
  conduct_certificate: "Conduct Certificate",
};

const FORM_TYPE_COLOR = {
  transfer_certificate: "blue",
  study_certificate: "green",
  conduct_certificate: "purple",
};

const CERT_TYPES = [
  { value: "transfer_certificate", label: "Transfer Certificate" },
  { value: "study_certificate",    label: "Study Certificate" },
  { value: "conduct_certificate",  label: "Conduct Certificate" },
];

// Sort order for status: pending first, then approved, then completed, then rejected
const STATUS_ORDER = { pending: 0, approved: 1, completed: 2, rejected: 3 };

const AdminCertParentRequests = () => {
  const user = useSelector(selectUser);
  const canApprove = user?.admin?.can_approve_transaction;
  const queryClient = useQueryClient();

  // Approve confirmation modal
  const [approveModal, setApproveModal] = useState(false);
  const [approveRecord, setApproveRecord] = useState(null);
  const [approving, setApproving] = useState(false);

  // Reject modal
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  // Mark Complete modal (upload signed PDF)
  const [completeModal, setCompleteModal] = useState(false);
  const [completeRecord, setCompleteRecord] = useState(null);
  const [completeFile, setCompleteFile] = useState(null);
  const [completeFileUrl, setCompleteFileUrl] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completeConfirmed, setCompleteConfirmed] = useState(false);

  // Search / filter
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterSource, setFilterSource] = useState(null);

  // Add Offline Request modal
  const [offlineModal, setOfflineModal] = useState(false);
  const [offlineClassId, setOfflineClassId] = useState(null);
  const [offlineStudentId, setOfflineStudentId] = useState(null);
  const [offlineCertType, setOfflineCertType] = useState(null);
  const [offlineReason, setOfflineReason] = useState("");
  const [submittingOffline, setSubmittingOffline] = useState(false);

  // â"€â"€â"€ Queries â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  const reqQuery = useQuery(
    ["cb-parent-requests", user?.school?._id],
    () =>
      axios
        .get(`${serverUrl}/cert-documents/all-requests?school=${user?.school?._id}`, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        })
        .then((r) => r.data),
    { enabled: !!user?.school?._id }
  );

  const classesQuery = useQuery("cb-classes", () =>
    axios.get(`${serverUrl}/classes/`, {
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    }).then((r) => r.data)
  );

  const studentsQuery = useQuery(
    ["cb-students-offline", offlineClassId],
    () =>
      axios.get(`${serverUrl}/attendance/students/class/${offlineClassId}`, {
        headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
      }).then((r) => r.data),
    { enabled: !!offlineClassId }
  );

  // â"€â"€â"€ Approve (simple confirmation) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  async function submitApproval() {
    setApproving(true);
    try {
      await axios.patch(
        `${serverUrl}/cert-documents/${approveRecord._id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      queryClient.invalidateQueries("cb-parent-requests");
      message.success("Request approved. It will appear in the Generate Certificates queue");
      setApproveModal(false);
      setApproveRecord(null);
    } catch (err) {
      message.error(err?.response?.data?.result?.message || "Failed to approve");
    } finally {
      setApproving(false);
    }
  }

  // â"€â"€â"€ Reject â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  async function submitRejection() {
    setRejecting(true);
    try {
      await axios.patch(
        `${serverUrl}/cert-documents/${rejectRecord._id}/reject`,
        { rejection_reason: rejectReason },
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      queryClient.invalidateQueries("cb-parent-requests");
      message.success("Request rejected");
      setRejectModal(false);
    } catch {
      message.error("Failed to reject request");
    } finally {
      setRejecting(false);
    }
  }

  // â"€â"€â"€ Mark Complete (upload signed PDF) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  function openCompleteModal(record) {
    setCompleteRecord(record);
    setCompleteFile(null);
    setCompleteFileUrl(null);
    setCompleteConfirmed(false);
    setCompleteModal(true);
  }

  function closeCompleteModal() {
    if (completeFileUrl) URL.revokeObjectURL(completeFileUrl);
    setCompleteModal(false);
    setCompleteRecord(null);
    setCompleteFile(null);
    setCompleteFileUrl(null);
    setCompleteConfirmed(false);
  }

  async function submitComplete() {
    if (!completeFile) return message.warning("Please upload the signed/sealed PDF");
    setCompleting(true);
    try {
      const formData = new FormData();
      formData.append("file", completeFile);
      await axios.patch(
        `${serverUrl}/cert-documents/${completeRecord._id}/complete`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      queryClient.invalidateQueries("cb-parent-requests");
      message.success("Certificate marked as completed - parent can now download");
      closeCompleteModal();
    } catch (err) {
      message.error(err?.response?.data?.result?.message || "Failed to complete request");
    } finally {
      setCompleting(false);
    }
  }

  // â"€â"€â"€ Offline Request â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  function resetOfflineModal() {
    setOfflineClassId(null);
    setOfflineStudentId(null);
    setOfflineCertType(null);
    setOfflineReason("");
    setOfflineModal(false);
  }

  async function submitOfflineRequest() {
    if (!offlineStudentId) return message.warning("Please select a student");
    if (!offlineCertType) return message.warning("Please select a certificate type");
    setSubmittingOffline(true);
    try {
      await axios.post(
        `${serverUrl}/cert-documents/request`,
        {
          student: offlineStudentId,
          form_type: offlineCertType,
          school: user?.school?._id,
          reason: offlineReason,
          request_source: "offline",
        },
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      queryClient.invalidateQueries("cb-parent-requests");
      message.success("Offline request added to the queue");
      resetOfflineModal();
    } catch (err) {
      const raw = err?.response?.data?.error?.errors?.[0];
      if (raw && raw.toLowerCase().includes("pending request")) {
        message.warning(
          "A pending request already exists for this student and certificate type. Please wait for the existing request to be processed.",
        );
      } else {
        message.error(raw || "Failed to add request");
      }
    } finally {
      setSubmittingOffline(false);
    }
  }

  // â"€â"€â"€ Table â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  const columns = [
    {
      title: "Student",
      key: "student",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#111", textTransform: "capitalize" }}>
            {r.student ? `${r.student.first_name} ${r.student.last_name}` : "-"}
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.student?.unique_id}</div>
        </div>
      ),
    },
    {
      title: "Certificate Type",
      dataIndex: "form_type",
      key: "form_type",
      render: (v) => <Tag color={FORM_TYPE_COLOR[v] || "default"}>{FORM_TYPE_LABEL[v] || v}</Tag>,
    },
    {
      title: "Source",
      dataIndex: "request_source",
      key: "request_source",
      render: (v) => (
        <Tag color={v === "offline" ? "purple" : "cyan"} style={{ textTransform: "capitalize" }}>
          {v === "offline" ? "Offline" : "Online"}
        </Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "request_reason",
      key: "request_reason",
      render: (v) => <span style={{ fontSize: 13, color: "#6b7280" }}>{v || "-"}</span>,
    },
    {
      title: "Requested On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "request_status",
      key: "request_status",
      render: (v) => (
        <Tag color={REQ_STATUS_COLOR[v] || "default"}>
          {REQ_STATUS_LABEL[v] || v}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, r) => {
        if (r.request_status === "pending") {
          return canApprove ? (
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button
                type="primary"
                size="small"
                style={{ background: "#16a34a", borderColor: "#16a34a" }}
                onClick={() => { setApproveRecord(r); setApproveModal(true); }}
              >
                Approve
              </Button>
              <Button danger size="small" onClick={() => { setRejectRecord(r); setRejectReason(""); setRejectModal(true); }}>
                Reject
              </Button>
            </div>
          ) : (
            <span style={{ fontSize: 12, color: "#9ca3af" }}>No approval rights</span>
          );
        }
        if (r.request_status === "approved") {
          return (
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button
                type="primary"
                size="small"
                style={{ background: "#f97316", borderColor: "#f97316" }}
                onClick={() => openCompleteModal(r)}
              >
                Mark Complete
              </Button>
            </div>
          );
        }
        if (r.request_status === "completed" && r.pdf_url) {
          return (
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button size="small" onClick={() => window.open(r.pdf_url, "_blank")}>View</Button>
              <Button
                size="small"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = r.pdf_url;
                  a.download = r.pdf_meta?.name || "certificate.pdf";
                  a.click();
                }}
              >
                Download
              </Button>
            </div>
          );
        }
        if (r.request_status === "rejected") {
          return <span style={{ fontSize: 12, color: "#ef4444" }}>{r.rejection_reason || "Rejected"}</span>;
        }
        return <span style={{ color: "#9ca3af" }}>-</span>;
      },
    },
  ];

  const allDocs = (reqQuery.data?.result?.data || [])
    .slice()
    .sort((a, b) => {
      const so = (STATUS_ORDER[a.request_status] ?? 9) - (STATUS_ORDER[b.request_status] ?? 9);
      if (so !== 0) return so;
      return new Date(a.createdAt) - new Date(b.createdAt);
    })
    .filter((r) => {
      const q = searchText.toLowerCase();
      const matchSearch = !q ||
        `${r.student?.first_name} ${r.student?.last_name}`.toLowerCase().includes(q) ||
        (r.student?.unique_id || "").toLowerCase().includes(q);
      const matchStatus = !filterStatus || r.request_status === filterStatus;
      const matchType = !filterType || r.form_type === filterType;
      const matchSource = !filterSource || r.request_source === filterSource;
      return matchSearch && matchStatus && matchType && matchSource;
    });

  // â"€â"€â"€ Render â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  return (
    <div className="mt-8">
      <div style={{ borderRadius: "10px", color: "#616161" }} className="bg-white drop-shadow-md p-8 w-full">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div className="text-2xl text-orange-500 font-semibold">Certificate Requests</div>
          <Button
            type="primary"
            size="large"
            style={{ background: "#f97316", borderColor: "#f97316" }}
            onClick={() => setOfflineModal(true)}
          >
            + Add Offline Request
          </Button>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            placeholder="Search by student name or ID"
            allowClear
            style={{ width: 240 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            allowClear
            showArrow
            placeholder="All Statuses"
            style={{ width: 200 }}
            value={filterStatus}
            onChange={setFilterStatus}
            getPopupContainer={(t) => t.parentNode}
          >
            <Option value="pending">Pending Approval</Option>
            <Option value="approved">Approved - Awaiting Generation</Option>
            <Option value="completed">Completed</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
          <Select
            allowClear
            showArrow
            placeholder="All Types"
            style={{ width: 190 }}
            value={filterType}
            onChange={setFilterType}
            getPopupContainer={(t) => t.parentNode}
          >
            <Option value="transfer_certificate">Transfer Certificate</Option>
            <Option value="study_certificate">Study Certificate</Option>
            <Option value="conduct_certificate">Conduct Certificate</Option>
          </Select>
          <Select
            allowClear
            showArrow
            placeholder="All Sources"
            style={{ width: 140 }}
            value={filterSource}
            onChange={setFilterSource}
            getPopupContainer={(t) => t.parentNode}
          >
            <Option value="online">Online</Option>
            <Option value="offline">Offline</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={allDocs}
          rowKey="_id"
          loading={reqQuery.isLoading}
          pagination={{ pageSize: 10 }}
        />
      </div>

      {/* â"€â"€ Approve Confirmation Modal â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      <Modal
        title="Approve Certificate Request"
        visible={approveModal}
        onCancel={() => { setApproveModal(false); setApproveRecord(null); }}
        footer={null}
        width={460}
      >
        {approveRecord && (
          <div style={{ paddingTop: 8 }}>
            <div style={{ background: "#f9fafb", borderRadius: 10, padding: "16px 20px", marginBottom: 20, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>Request details</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>
                {approveRecord.student?.first_name} {approveRecord.student?.last_name}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>{FORM_TYPE_LABEL[approveRecord.form_type]}</div>
              {approveRecord.request_reason && (
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>Reason: {approveRecord.request_reason}</div>
              )}
            </div>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              Approving this request will queue it in the <strong>Generate Certificates</strong> tab for PDF generation.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <Button size="large" onClick={() => { setApproveModal(false); setApproveRecord(null); }} style={{ paddingLeft: 24, paddingRight: 24 }}>Cancel</Button>
              <Button
                type="primary"
                size="large"
                loading={approving}
                onClick={submitApproval}
                style={{ background: "#16a34a", borderColor: "#16a34a", paddingLeft: 32, paddingRight: 32 }}
              >
                Approve Request
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* â"€â"€ Reject Modal â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      <Modal
        title="Reject Certificate Request"
        visible={rejectModal}
        onCancel={() => setRejectModal(false)}
        footer={null}
        width={420}
      >
        <div style={{ paddingTop: 8 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>Reason for rejection</label>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason..."
              style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", resize: "none", outline: "none", fontSize: 13 }}
            />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Button size="large" onClick={() => setRejectModal(false)} style={{ paddingLeft: 24, paddingRight: 24 }}>Cancel</Button>
            <Button type="primary" danger size="large" loading={rejecting} onClick={submitRejection} style={{ paddingLeft: 32, paddingRight: 32 }}>
              Reject Request
            </Button>
          </div>
        </div>
      </Modal>

      {/* â"€â"€ Mark Complete Modal â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      <Modal
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Mark Request as Completed</div>
            {completeRecord && (
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 400, marginTop: 4 }}>
                Upload the signed/sealed PDF for {completeRecord.student?.first_name} {completeRecord.student?.last_name} - {FORM_TYPE_LABEL[completeRecord?.form_type]}
              </div>
            )}
          </div>
        }
        visible={completeModal}
        onCancel={closeCompleteModal}
        footer={null}
        width={680}
      >
        <div style={{ paddingTop: 8 }}>
          <Dragger
            accept=".pdf"
            maxCount={1}
            beforeUpload={(file) => {
              if (completeFileUrl) URL.revokeObjectURL(completeFileUrl);
              setCompleteFile(file);
              setCompleteFileUrl(URL.createObjectURL(file));
              return false;
            }}
            onRemove={() => {
              if (completeFileUrl) URL.revokeObjectURL(completeFileUrl);
              setCompleteFile(null);
              setCompleteFileUrl(null);
            }}
            fileList={completeFile ? [completeFile] : []}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the signed/sealed PDF here</p>
            <p className="ant-upload-hint" style={{ color: "#9ca3af" }}>This PDF will be available for the parent to download</p>
          </Dragger>

          {completeFileUrl && (
            <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb", padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "#374151" }}>
                PDF Preview
              </div>
              <iframe src={completeFileUrl} title="PDF Preview" style={{ width: "100%", height: 400, border: "none" }} />
            </div>
          )}

          <div style={{ marginTop: 20, padding: "14px 16px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 8 }}>
            <Checkbox
              checked={completeConfirmed}
              onChange={(e) => setCompleteConfirmed(e.target.checked)}
            >
              <span style={{ fontSize: 13, color: "#92400e", fontWeight: 500 }}>
                I confirm that the certificate has been verified, signed by the authorized signatory, and stamped with the official school seal. It is ready for parent viewing and download.
              </span>
            </Checkbox>
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <Button size="large" onClick={closeCompleteModal} style={{ paddingLeft: 24, paddingRight: 24 }}>Cancel</Button>
            <Button
              type="primary"
              size="large"
              loading={completing}
              onClick={submitComplete}
              disabled={!completeConfirmed || !completeFile}
              style={{
                background: completeConfirmed && completeFile ? "#f97316" : undefined,
                borderColor: completeConfirmed && completeFile ? "#f97316" : undefined,
                paddingLeft: 32,
                paddingRight: 32,
              }}
            >
              Upload & Mark Complete
            </Button>
          </div>
        </div>
      </Modal>

      {/* â"€â"€ Offline Request Modal â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      <Modal
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Add Offline / Walk-in Request</div>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 400, marginTop: 4 }}>
              For walk-in or phone-based certificate requests not submitted through the app
            </div>
          </div>
        }
        visible={offlineModal}
        onCancel={resetOfflineModal}
        footer={null}
        width={480}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 8 }}>
              1 Class
            </label>
            <Select
              showSearch
              showArrow
              style={{ width: "100%" }}
              placeholder="Select class"
              optionFilterProp="children"
              onChange={(v) => { setOfflineClassId(v); setOfflineStudentId(null); }}
              getPopupContainer={(t) => t.parentNode}
              size="large"
            >
              {(classesQuery.data || []).map((c) => (
                <Option key={c._id} value={c._id}>{c.name}</Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: offlineClassId ? "#6b7280" : "#d1d5db", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 8 }}>
              2 Student
            </label>
            <Select
              showSearch
              showArrow
              style={{ width: "100%" }}
              placeholder={offlineClassId ? "Select student" : "Select a class first"}
              optionFilterProp="children"
              onChange={(v) => setOfflineStudentId(v)}
              value={offlineStudentId}
              disabled={!offlineClassId}
              getPopupContainer={(t) => t.parentNode}
              size="large"
            >
              {(studentsQuery.data?.data || []).map((s) => (
                <Option key={s._id} value={s._id}>{s.first_name} {s.last_name}</Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 8 }}>
              3 Certificate Type
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select certificate type"
              onChange={(v) => setOfflineCertType(v)}
              value={offlineCertType}
              getPopupContainer={(t) => t.parentNode}
              size="large"
            >
              {CERT_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 8 }}>
              Reason / Note (optional)
            </label>
            <textarea
              rows={2}
              value={offlineReason}
              onChange={(e) => setOfflineReason(e.target.value)}
              placeholder="e.g. Walk-in needs TC for new school admission"
              style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", resize: "none", outline: "none", fontSize: 13 }}
            />
          </div>

          <div style={{ display: "flex", gap: 12, paddingTop: 4 }}>
            <Button size="large" onClick={resetOfflineModal} style={{ paddingLeft: 24, paddingRight: 24 }}>Cancel</Button>
            <Button
              type="primary"
              size="large"
              loading={submittingOffline}
              onClick={submitOfflineRequest}
              style={{ background: "#f97316", borderColor: "#f97316", flex: 1 }}
            >
              Add Request
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminCertParentRequests;

